import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MasterModel } from '../../master/models/master.model';
import { MasterService } from '../../master/services/master.service';

export interface ProjectLookupOption {
  id: string;
  display: string;
}

export interface ProjectFormLookups {
  categories: ProjectLookupOption[];
  riskLevels: ProjectLookupOption[];
  countries: ProjectLookupOption[];
  provinces: ProjectLookupOption[];
}

@Injectable({ providedIn: 'root' })
export class ProjectLookupsFacade {
  constructor(private masterService: MasterService) { }

  formLookups(): Promise<ProjectFormLookups> {
    return firstValueFrom(forkJoin({
      categories: this.normalized(this.masterService.getProjectCategories()),
      riskLevels: this.normalized(this.masterService.getProjectRiskLevels()),
      countries: this.normalized(this.masterService.getCountries()),
      provinces: this.normalized(this.masterService.getProvinces()),
    }));
  }

  categories(): Promise<ProjectLookupOption[]> {
    return firstValueFrom(this.normalized(this.masterService.getProjectCategories()));
  }

  municipalities(provinceCode: string): Promise<ProjectLookupOption[]> {
    if (!provinceCode) return Promise.resolve([]);
    return firstValueFrom(this.normalized(this.masterService.getMunicipalities(provinceCode)));
  }

  projects(): Promise<ProjectLookupOption[]> {
    return firstValueFrom(this.normalized(this.masterService.getProjects()));
  }

  private normalized(source: Observable<any>): Observable<ProjectLookupOption[]> {
    return source.pipe(map(response => {
      const list: MasterModel[] = Array.isArray(response)
        ? response
        : (response && response.isSuccess !== false && Array.isArray(response.value) ? response.value : []);
      return list
        .filter(item => !!item && item.id !== null && item.id !== undefined)
        .map(item => ({ id: String(item.id), display: item.display || String(item.id) }));
    }));
  }
}
